import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Settings, Heart } from 'lucide-react';
import GrainOverlay from './GrainOverlay';

const About: React.FC = () => {
  const values = [
    {
      icon: Shield,
      title: 'Security First',
      description: 'Enterprise-grade security and compliance'
    },
    {
      icon: Settings,
      title: 'Modular Design',
      description: 'Use what you need, scale as you grow'
    },
    {
      icon: Heart,
      title: 'Healthcare Focus',
      description: 'Built specifically for healthcare workflows'
    }
  ];

  const techStack = [
    'AI/ML', 'Cloud Native', 'Secure & Compliant', 'Real-time', 'Mobile Ready', 'API First'
  ];

  const timelineItems = [
    {
      type: 'about' as const,
      content: {
        title: 'About BioAxis',
        paragraphs: [
          'Built by engineers and healthcare consultants, BioAxis combines deep domain knowledge with cutting-edge software engineering. We\'re headquartered in South Africa and serve healthcare professionals worldwide.',
          'Every product is standalone, modular, and built to scale -- offering medical practices, pharmacies, physiotherapy clinics, and bioscience teams the freedom to adopt what they need, when they need it.'
        ]
      }
    },
    ...values.map(v => ({
      type: 'value' as const,
      content: v
    }))
  ];

  return (
    <section id="about" className="py-20 relative overflow-hidden">
      <GrainOverlay />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-secondary-500" />

          <div className="space-y-12">
            {timelineItems.map((item, index) => (
              <motion.div
                key={index}
                className="relative pl-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                {/* Timeline node */}
                <div className="absolute left-0 top-1.5 w-4 h-4 bg-secondary-500 rounded-full border-2 border-white dark:border-[#0f172a]" />

                {item.type === 'about' ? (
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-6 font-display">
                      {item.content.title}
                    </h2>
                    <div className="space-y-4">
                      {(item.content as any).paragraphs.map((p: string, i: number) => (
                        <p key={i} className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
                          {p}
                        </p>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="glass-effect rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-secondary-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        {React.createElement((item.content as any).icon, { className: "w-5 h-5 text-white" })}
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                          {(item.content as any).title}
                        </h4>
                        <p className="text-slate-600 dark:text-slate-300">
                          {(item.content as any).description}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Tech stack badges */}
        <motion.div
          className="mt-16 pl-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="flex flex-wrap gap-3">
            {techStack.map((tech, index) => (
              <span
                key={index}
                className="px-4 py-2 rounded-full text-sm font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700"
              >
                {tech}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
